/**
 * Helpers d'animation.
 *
 * ---------------------------------------------------------------------------
 * HONNETETE DE L'AFFICHAGE — regle non negociable
 * ---------------------------------------------------------------------------
 * Aucune animation de ce projet ne fabrique de "near miss" : pas de rouleau qui
 * ralentit sur un symbole gagnant avant de glisser d'un cran, pas de suspense
 * artificiel devant un resultat deja perdant, pas de mise en scene qui suggere
 * un gain que le serveur n'a pas accorde.
 *
 * Le serveur decide, le client montre. Une animation ne peut que RETARDER
 * l'affichage d'un resultat deja connu, jamais en changer la lecture. Les
 * durees sont volontairement courtes (150-250 ms) pour que l'ecart entre
 * "le serveur a repondu" et "je le vois" reste imperceptible.
 * ---------------------------------------------------------------------------
 *
 * Toutes les fonctions ici respectent "animations reduites" : quand le mode est
 * actif, l'etat final est applique immediatement, sans transition ni rAF.
 */

export type ReducedMotionMode = 'auto' | 'on' | 'off';

/** Courbes maison : rien de lineaire, rien de rebondissant. */
export const EASING = {
  /** Sortie douce, pour tout ce qui apparait. */
  out: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
  /** Entree seche, pour tout ce qui disparait. */
  in: 'cubic-bezier(0.55, 0.06, 0.68, 0.19)',
  /** Symetrique, pour les deplacements. */
  inOut: 'cubic-bezier(0.65, 0, 0.35, 1)',
} as const;

export const DURATION = {
  micro: 150,
  base: 200,
  screen: 260,
} as const;

const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
let mode: ReducedMotionMode = 'auto';
const listeners = new Set<(reduced: boolean) => void>();

function notify(): void {
  const reduced = isReducedMotion();
  for (const listener of [...listeners]) listener(reduced);
}

mediaQuery.addEventListener('change', () => {
  if (mode === 'auto') notify();
});

/** `auto` suit `prefers-reduced-motion`, qui est la valeur par defaut. */
export function setReducedMotionMode(next: ReducedMotionMode): void {
  if (mode === next) return;
  mode = next;
  document.documentElement.dataset['motion'] = isReducedMotion() ? 'reduced' : 'full';
  notify();
}

export function isReducedMotion(): boolean {
  if (mode === 'on') return true;
  if (mode === 'off') return false;
  return mediaQuery.matches;
}

export function onReducedMotionChange(listener: (reduced: boolean) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Synchronise l'attribut CSS au demarrage. */
export function syncMotionAttribute(): void {
  document.documentElement.dataset['motion'] = isReducedMotion() ? 'reduced' : 'full';
}

export interface AnimateOptions {
  readonly duration?: number;
  readonly easing?: string;
  readonly delay?: number;
  readonly fill?: FillMode;
}

/**
 * Enveloppe de l'API Web Animations. Renvoie null si les animations sont
 * reduites : l'appelant ne doit donc jamais dependre du retour pour appliquer
 * un etat final.
 */
export function animate(
  target: Element,
  keyframes: readonly Keyframe[],
  options: AnimateOptions = {},
): Animation | null {
  if (isReducedMotion()) return null;
  return target.animate([...keyframes], {
    duration: options.duration ?? DURATION.base,
    easing: options.easing ?? EASING.out,
    delay: options.delay ?? 0,
    fill: options.fill ?? 'both',
  });
}

/** Apparition d'un element, avec decalage optionnel pour les grilles. */
export function enter(target: Element, index = 0): void {
  animate(
    target,
    [
      { opacity: 0, transform: 'translateY(10px) scale(0.995)' },
      { opacity: 1, transform: 'none' },
    ],
    { duration: DURATION.base, delay: Math.min(index * 45, 270), easing: EASING.out },
  );
}

/** Sortie d'un element ; la promesse se resout meme en mode reduit. */
export async function exit(target: Element): Promise<void> {
  const animation = animate(
    target,
    [
      { opacity: 1, transform: 'none' },
      { opacity: 0, transform: 'translateY(-6px)' },
    ],
    { duration: DURATION.micro, easing: EASING.in },
  );
  if (!animation) return;
  try {
    await animation.finished;
  } catch {
    // Animation annulee (ecran demonte) : rien a faire.
  }
}

export function shake(target: Element): void {
  animate(
    target,
    [
      { transform: 'translateX(0)' },
      { transform: 'translateX(-5px)' },
      { transform: 'translateX(4px)' },
      { transform: 'translateX(-2px)' },
      { transform: 'translateX(0)' },
    ],
    { duration: 220, easing: EASING.inOut },
  );
}

// ---------------------------------------------------------------------------
// Formatage et comptage de jetons
// ---------------------------------------------------------------------------

const grouped = new Intl.NumberFormat('fr-FR');

/**
 * Format des jetons. Sous 10 000 on garde les chiffres colles : les petits
 * soldes restent compacts et lisibles d'un coup d'oeil.
 */
export function formatChips(value: number): string {
  const rounded = Math.round(value);
  return Math.abs(rounded) < 10_000 ? String(rounded) : grouped.format(rounded);
}

export interface CountOptions {
  readonly duration?: number;
  readonly format?: (value: number) => string;
  readonly onDone?: () => void;
}

const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;

/**
 * Compte de `from` vers `to` dans un element texte.
 *
 * Ce n'est PAS une simulation : `to` est deja la valeur que le serveur a
 * renvoyee. Le comptage rend la variation lisible, il ne l'invente pas.
 */
export function countTo(
  target: Element,
  from: number,
  to: number,
  options: CountOptions = {},
): () => void {
  const format = options.format ?? formatChips;

  if (isReducedMotion() || from === to) {
    target.textContent = format(to);
    options.onDone?.();
    return () => {};
  }

  const delta = Math.abs(to - from);
  const duration = options.duration ?? Math.min(1100, 380 + delta * 0.9);
  const start = performance.now();
  let frame = 0;
  let cancelled = false;

  const step = (now: number): void => {
    if (cancelled) return;
    const progress = Math.min(1, (now - start) / duration);
    const value = from + (to - from) * easeOutCubic(progress);
    target.textContent = format(value);
    if (progress < 1) {
      frame = requestAnimationFrame(step);
    } else {
      target.textContent = format(to);
      options.onDone?.();
    }
  };

  frame = requestAnimationFrame(step);
  return () => {
    cancelled = true;
    cancelAnimationFrame(frame);
    target.textContent = format(to);
  };
}

/** Duree lisible « 3 h 12 min », « 4 min 05 s ». */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return `${hours} h ${String(minutes).padStart(2, '0')} min`;
  if (minutes > 0) return `${minutes} min ${String(seconds).padStart(2, '0')} s`;
  return `${seconds} s`;
}

/** Chronometre de session : « 00:42:07 ». */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = String(Math.floor(total / 3600)).padStart(2, '0');
  const minutes = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
  const seconds = String(total % 60).padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
}
